import AddIcon from "@mui/icons-material/Add";
import {
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import reorderDnD from "common/utils/reorderDnD";
import React from "react";
import { DragDropContext, DropResult, Droppable } from "react-beautiful-dnd";
import FieldOptionRow from "./FieldOptionRow";
import { SelectOption } from "./OptionsSection";

type FieldOptionsBasicFormProps = {
  selectOptions: SelectOption[];
  onChange: (newSelectOptions: SelectOption[]) => void;
  isShowLabelsEnabled: boolean;
};

const FieldOptionsBasicForm: React.FC<FieldOptionsBasicFormProps> = ({
  selectOptions,
  onChange,
  isShowLabelsEnabled,
}) => {
  const onDragEnd = (result: DropResult) => {
    const { destination, source } = result;

    if (
      !destination ||
      (source.index === destination.index &&
        source.droppableId === destination.droppableId)
    ) {
      return;
    }

    const newOrderedOptions = reorderDnD<SelectOption>(
      selectOptions!,
      source.index,
      destination.index
    );

    onChange(newOrderedOptions);
  };

  const handleClickAddOption = (event: React.MouseEvent<HTMLButtonElement>) => {
    const newOption: SelectOption = {
      value: `Option ${(selectOptions ?? []).length + 1}`,
    };

    onChange([...(selectOptions ?? []), newOption]);
  };

  const handleClickDeleteOption =
    (index: React.Key) => (event: React.MouseEvent<HTMLButtonElement>) => {
      const newSelectOptions = selectOptions!.filter((_, i) => i !== index);
      onChange(newSelectOptions);
    };

  const NoRows = () => {
    return (
      <TableRow>
        <TableCell
          colSpan={12}
          align="center"
          sx={{ border: "none" }}
          height={1}
        >
          <Typography variant="caption" textAlign="center" width={1}>
            Please add Options
          </Typography>
        </TableCell>
      </TableRow>
    );
  };

  const handleChangeOption = (index: number, newSelectOption: SelectOption) => {
    const newOptions = structuredClone(selectOptions);

    newOptions[index] = newSelectOption;

    onChange(newOptions);
  };

  const hasSelectOptions: boolean = !!selectOptions && selectOptions.length > 0;

  return (
    <Stack sx={{ maxHeight: 300, width: 1, overflowY: "auto" }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell align="left" padding="none" />
            <TableCell>Value</TableCell>
            {isShowLabelsEnabled && <TableCell>Label</TableCell>}
            <TableCell align="right" padding="none">
              <IconButton size="small" onClick={handleClickAddOption}>
                <Tooltip title="Add Option">
                  <AddIcon fontSize="small" />
                </Tooltip>
              </IconButton>
            </TableCell>
          </TableRow>
        </TableHead>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="options-config-list">
            {(droppableProvider) => (
              <TableBody
                {...droppableProvider.droppableProps}
                ref={droppableProvider.innerRef}
              >
                {!hasSelectOptions ? (
                  <NoRows />
                ) : (
                  selectOptions.map((option, index) => (
                    <FieldOptionRow
                      option={option}
                      key={index}
                      index={index}
                      onClickDeleteOption={handleClickDeleteOption}
                      onChangeOption={handleChangeOption}
                      showLabels={isShowLabelsEnabled}
                    />
                  ))
                )}
                {droppableProvider.placeholder}
              </TableBody>
            )}
          </Droppable>
        </DragDropContext>
      </Table>
    </Stack>
  );
};

export default FieldOptionsBasicForm;
